import { z } from 'zod';
import { ATTENDANCE_REQUEST_STATUSES, DAY_STATUSES, DEVICE_KINDS, GEOFENCE_RESULTS, PUNCH_DIRECTIONS, PUNCH_FLAGS, PUNCH_MODES, PUNCH_SOURCES, } from '../attendance/enums.js';
import { normalizeCidr } from '../geo.js';
import { isoDate, paginated, paginationQuerySchema, reasonText, uuid } from './common.js';
/** Office locations & geofence (FR-ATT-2, FR-ATT-4, SRS §17, ADR-080). */
/** Six decimals (≈ 0.1 m), what `office_locations` stores. */
const coordinate = (min, max) => z
    .number()
    .min(min)
    .max(max)
    .transform((v) => Math.round(v * 1e6) / 1e6);
/** Office public IPs: CIDR ranges or single addresses, normalised (host bits cleared). */
const ipRanges = cidrList(20);
function cidrList(max) {
    return z
        .array(z.string().trim().min(1).max(64))
        .max(max)
        .transform((items, ctx) => {
        const out = [];
        items.forEach((item, i) => {
            const range = normalizeCidr(item);
            if (range === null) {
                ctx.addIssue({
                    code: 'custom',
                    path: [i],
                    message: `“${item}” is not an IP address or range, e.g. 203.0.113.0/24`,
                });
            }
            else if (!out.includes(range)) {
                out.push(range);
            }
        });
        return out;
    });
}
const officeFields = z.object({
    name: z.string().trim().min(2).max(120),
    latitude: coordinate(-90, 90),
    longitude: coordinate(-180, 180),
    /** Geofence radius in metres. */
    radiusM: z.int().min(25).max(5_000),
    ipRanges,
    active: z.boolean(),
});
/** `POST /attendance/offices`; the radius defaults to `geofence_default_radius_m`. */
export const officeCreateSchema = officeFields.extend({
    radiusM: officeFields.shape.radiusM.optional(),
    ipRanges: ipRanges.default([]),
    active: z.boolean().default(true),
});
/** `PATCH /attendance/offices/:id`: any subset. Offices are deactivated, never deleted. */
export const officeUpdateSchema = officeFields
    .partial()
    .refine((v) => Object.keys(v).length > 0, 'Nothing to update');
export const officeItemSchema = z.object({
    id: uuid,
    name: z.string(),
    latitude: z.number(),
    longitude: z.number(),
    radiusM: z.int(),
    ipRanges: z.array(z.string()),
    active: z.boolean(),
});
/** `GET /attendance/offices`: every office (active first) and the radius new offices get. */
export const officeListResponseSchema = z.object({
    items: z.array(officeItemSchema),
    defaultRadiusM: z.int(),
});
// ── App check-in / out (FR-ATT-3, FR-ATT-4, ADR-056, ADR-081) ────────────────────────────────
/** Coarser fixes count the same (the accuracy credit is capped at an office's radius). */
const MAX_ACCURACY_M = 10_000;
/**
 * `POST /attendance/punch`: the direction and mode the employee chose and the browser's fix at
 * that moment (all three, or none when location is denied). There is no time field — the server
 * time is the punch time, and anything else sent is dropped.
 */
export const punchCreateSchema = z
    .object({
    direction: z.enum(PUNCH_DIRECTIONS),
    mode: z.enum(PUNCH_MODES),
    lat: coordinate(-90, 90).optional(),
    lng: coordinate(-180, 180).optional(),
    /** Metres, as `GeolocationCoordinates.accuracy` reports it; rounded to 0.1 m. */
    accuracy: z
        .number()
        .min(0)
        .transform((v) => Math.min(Math.round(v * 10) / 10, MAX_ACCURACY_M))
        .optional(),
})
    .refine((v) => (v.lat === undefined) === (v.lng === undefined), {
    path: ['lng'],
    message: 'Send latitude and longitude together',
})
    .refine((v) => v.accuracy === undefined || v.lat !== undefined, {
    path: ['accuracy'],
    message: 'Accuracy comes with a location',
});
/** A punch as its employee sees it (no coordinates or IP echoed back). */
export const punchItemSchema = z.object({
    id: uuid,
    /** Server time, ISO instant. */
    punchedAt: z.string(),
    direction: z.enum(PUNCH_DIRECTIONS),
    mode: z.enum(PUNCH_MODES),
    source: z.enum(PUNCH_SOURCES),
    geofence: z.enum(GEOFENCE_RESULTS),
    flags: z.array(z.enum(PUNCH_FLAGS)),
});
/** `GET /attendance/today`: the caller's punches today (Asia/Dhaka) for the today card. */
export const attendanceTodaySchema = z.object({
    date: isoDate,
    /** Today's punches, oldest first; voided ones left out. */
    punches: z.array(punchItemSchema),
    /** What the card offers next: check-in when there is no punch or the last was a check-out. */
    nextDirection: z.enum(PUNCH_DIRECTIONS),
    /** An approved WFH covers today, so a WFH punch is not flagged (FR-WFH-4). */
    wfhApproved: z.boolean(),
    /** Settings `require_location`: a punch without location is flagged `NO_LOCATION`. */
    requireLocation: z.boolean(),
});
// ── Biometric devices (FR-ATT-5, FR-ATT-6, ADR-056, ADR-082) ─────────────────────────────────
/** A device's own user id (ZKTeco "PIN"); by default the Emp. Code. */
export const deviceUserId = z
    .string()
    .trim()
    .min(1)
    .max(32)
    .regex(/^[\w.-]+$/, 'Letters, digits, dot, dash or underscore');
const deviceFields = z.object({
    name: z.string().trim().min(2).max(120),
    /** ZKTeco: Menu › System Info › Device Info › Serial Number. */
    serialNumber: z
        .string()
        .trim()
        .toUpperCase()
        .regex(/^[A-Z0-9-]{4,40}$/, 'The serial number from the device’s Device Info screen')
        .nullable(),
    /** Where the device calls from, e.g. its fixed LAN address. */
    allowedIps: cidrList(10),
    active: z.boolean(),
});
/** `POST /attendance/devices`. ADMS needs the serial number and at least one source IP. */
export const deviceCreateSchema = deviceFields
    .extend({
    kind: z.enum(DEVICE_KINDS),
    serialNumber: deviceFields.shape.serialNumber.default(null),
    allowedIps: deviceFields.shape.allowedIps.default([]),
    active: z.boolean().default(true),
})
    .superRefine((v, ctx) => {
    if (v.kind !== 'ZKTECO_ADMS')
        return;
    if (v.serialNumber === null) {
        ctx.addIssue({
            code: 'custom',
            path: ['serialNumber'],
            message: 'Enter the serial number from the device’s Device Info screen',
        });
    }
    if (v.allowedIps.length === 0) {
        ctx.addIssue({
            code: 'custom',
            path: ['allowedIps'],
            message: 'Add the device’s LAN address, e.g. 192.168.0.201',
        });
    }
});
/** `PATCH /attendance/devices/:id`: any subset (the kind is fixed). */
export const deviceUpdateSchema = deviceFields
    .partial()
    .refine((v) => Object.keys(v).length > 0, 'Nothing to update');
export const deviceItemSchema = z.object({
    id: uuid,
    name: z.string(),
    /** A `DEVICE_KINDS` value, or the kind of an adapter added in code (e.g. a test fake). */
    kind: z.string(),
    serialNumber: z.string().nullable(),
    allowedIps: z.array(z.string()),
    active: z.boolean(),
    /** Last time the device called in (handshake, poll or upload). */
    lastSeenAt: z.string().nullable(),
    /** Punches from this device waiting for a user mapping. */
    unmappedCount: z.int(),
});
export const deviceListResponseSchema = z.object({ items: z.array(deviceItemSchema) });
/** A new device, or a rotated secret: the secret is shown this once (GENERIC_PUSH only). */
export const deviceWithSecretSchema = z.object({
    device: deviceItemSchema,
    secret: z.string().nullable(),
});
/** `POST /attendance/devices/:id/map`: this device user is this employee (overrides Emp. Code). */
export const deviceMapCreateSchema = z.object({ deviceUserId, employeeId: uuid });
export const deviceMapItemSchema = z.object({
    id: uuid,
    deviceUserId: z.string(),
    employee: z.object({ id: uuid, empCode: z.int(), fullName: z.string() }),
});
export const deviceMapListResponseSchema = z.object({ items: z.array(deviceMapItemSchema) });
/** The result of mapping: the override and how many queued punches it picked up. */
export const deviceMapResultSchema = z.object({
    mapping: deviceMapItemSchema,
    attachedPunches: z.int(),
});
/** `GET /attendance/devices/unmapped`: the HR queue, one row per device and device user. */
export const unmappedDeviceUserSchema = z.object({
    deviceId: uuid,
    deviceName: z.string(),
    deviceUserId: z.string(),
    punches: z.int(),
    firstPunchAt: z.string(),
    lastPunchAt: z.string(),
});
export const unmappedListResponseSchema = z.object({ items: z.array(unmappedDeviceUserSchema) });
/**
 * `POST /attendance/devices/:id/punches` (GENERIC_PUSH): up to 500 punches; an instant with its
 * offset (or a Dhaka local time); the direction when the device knows it.
 */
export const devicePushSchema = z.object({
    punches: z
        .array(z.object({
        deviceUserId,
        punchedAt: z.union([z.iso.datetime({ offset: true }), z.iso.datetime({ local: true })]),
        direction: z.enum(PUNCH_DIRECTIONS).optional(),
    }))
        .min(1)
        .max(500),
});
/** What an upload did: stored, already there (deduplicated), stored without an employee. */
export const deviceIngestResultSchema = z.object({
    received: z.int(),
    inserted: z.int(),
    duplicates: z.int(),
    unmapped: z.int(),
});
// ── Daily records (FR-ATT-7, FR-ATT-8, ADR-083) ──────────────────────────────────────────────
/** Longest range one recompute call covers. */
export const MAX_RECOMPUTE_DAYS = 62;
const dayCount = (from, to) => (Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000 + 1;
/**
 * `POST /attendance/recompute`: recompute daily records for a date or range (≤ 62 days, not
 * after today) — everyone on the roster, or one employee.
 */
export const attendanceRecomputeSchema = z
    .object({
    from: isoDate,
    to: isoDate.optional(),
    employeeId: uuid.optional(),
})
    .refine((v) => (v.to ?? v.from) >= v.from, { path: ['to'], message: 'Ends before it starts' })
    .refine((v) => dayCount(v.from, v.to ?? v.from) <= MAX_RECOMPUTE_DAYS, {
    path: ['to'],
    message: `At most ${String(MAX_RECOMPUTE_DAYS)} days at a time`,
});
/** What a recompute did: days looked at, records written (new or changed), records removed. */
export const attendanceRecomputeResultSchema = z.object({
    from: isoDate,
    to: isoDate,
    days: z.int(),
    written: z.int(),
    removed: z.int(),
});
// ── Regularisation & HR corrections (FR-ATT-9, FR-ATT-10, ADR-084) ──────────────────────────
/** How far back an employee may ask for a correction (owner); older days go through HR. */
export const REGULARISATION_WINDOW_DAYS = 30;
/** `HH:MM` on the day, Asia/Dhaka. */
const hhmm = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Use HH:MM, e.g. 09:45');
/**
 * `POST /attendance/regularisations`: a past date (yesterday … 30 days back) with the corrected
 * check-in and / or check-out and a reason.
 */
export const regularisationCreateSchema = z
    .object({
    date: isoDate,
    checkIn: hhmm.optional(),
    checkOut: hhmm.optional(),
    reason: reasonText,
})
    .refine((v) => v.checkIn !== undefined || v.checkOut !== undefined, {
    path: ['checkIn'],
    message: 'Give the check-in, the check-out or both',
})
    .refine((v) => !v.checkIn || !v.checkOut || v.checkIn < v.checkOut, {
    path: ['checkOut'],
    message: 'Check-out must be after check-in',
});
const personRef = z.object({ id: uuid, empCode: z.int(), fullName: z.string() });
export const regularisationItemSchema = z.object({
    id: uuid,
    employee: personRef,
    date: isoDate,
    checkIn: z.string().nullable(),
    checkOut: z.string().nullable(),
    reason: z.string(),
    status: z.enum(ATTENDANCE_REQUEST_STATUSES),
    requestedAt: z.string(),
    /** The approval behind it (opens in the approvals inbox). */
    approvalRequestId: uuid.nullable(),
    /** The caller may withdraw it now (own, pending). */
    canCancel: z.boolean(),
});
export const regularisationScopeSchema = z.enum(['me', 'all']);
/** `GET /attendance/regularisations?scope=me|all`, newest first. */
export const regularisationListQuerySchema = paginationQuerySchema.extend({
    scope: regularisationScopeSchema.default('me'),
});
export const regularisationListResponseSchema = paginated(regularisationItemSchema);
/** `POST /attendance/punches` (HR): a punch for someone at a time on a day up to now. */
export const punchAddSchema = z.object({
    employeeId: uuid,
    date: isoDate,
    time: hhmm,
    direction: z.enum(PUNCH_DIRECTIONS),
    mode: z.enum(PUNCH_MODES).default('OFFICE'),
    reason: reasonText,
});
/** `POST /attendance/punches/:id/void`: the punch stays, marked void with the reason. */
export const punchVoidSchema = z.object({ reason: reasonText });
/** A flagged punch in HR's review queue (coordinates for `attendance.read.all`, AC-9). */
export const flaggedPunchSchema = punchItemSchema.extend({
    employee: personRef,
    latitude: z.number().nullable(),
    longitude: z.number().nullable(),
    accuracyM: z.number().nullable(),
});
/** `GET /attendance/punches/flagged`: oldest first. */
export const flaggedPunchListQuerySchema = paginationQuerySchema;
export const flaggedPunchListResponseSchema = paginated(flaggedPunchSchema);
// ── Attendance views (FR-ATT-11, FR-ATT-12, AC-9, AC-10, ADR-085) ────────────────────────────
/** `YYYY-MM`. */
const month = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Use YYYY-MM');
/** A month's counts (owner: present includes late; leave days count half days as 0.5). */
export const attendanceTotalsSchema = z.object({
    /** Working days (work week, not a holiday) on the roster, up to today. */
    workingDays: z.int(),
    /** PRESENT + LATE. */
    present: z.int(),
    late: z.int(),
    halfDay: z.int(),
    incomplete: z.int(),
    absent: z.int(),
    wfh: z.int(),
    /** ON_LEAVE = 1, HALF_LEAVE = 0.5. */
    leave: z.number(),
    lateMinutes: z.int(),
});
/** A stored daily record (FR-ATT-8); null fields while there is none. */
export const dayRecordSchema = z.object({
    date: isoDate,
    status: z.enum(DAY_STATUSES).nullable(),
    firstIn: z.string().nullable(),
    lastOut: z.string().nullable(),
    workedMinutes: z.int(),
    lateMinutes: z.int(),
});
export const monthQuerySchema = z.object({ month });
/** `GET /me/attendance?month=`: every date of the month with its record and punches. */
export const myAttendanceMonthSchema = z.object({
    month,
    days: z.array(dayRecordSchema.extend({ punches: z.array(punchItemSchema) })),
    totals: attendanceTotalsSchema,
});
const memberSchema = personRef.extend({ designation: z.string().nullable() });
/** Where someone is today (FR-ATT-11): from their punches, leave, WFH and the calendar. */
export const TODAY_STATES = [
    'IN',
    'LEFT',
    'WFH',
    'ON_LEAVE',
    'HALF_LEAVE',
    'NOT_CHECKED_IN',
    'OFF',
];
/** `GET /attendance/team/today` (team.read): times, mode, geofence result — no location (AC-9). */
export const teamTodaySchema = z.object({
    date: isoDate,
    items: z.array(z.object({
        employee: memberSchema,
        state: z.enum(TODAY_STATES),
        firstIn: z.string().nullable(),
        lastPunchAt: z.string().nullable(),
        mode: z.enum(PUNCH_MODES).nullable(),
        /** Geofence result of the first check-in (AC-9 allows managers this, not coordinates). */
        geofence: z.enum(GEOFENCE_RESULTS).nullable(),
    })),
});
/** `GET /attendance/team?month=` and `GET /attendance/records?month=`: a month grid. */
export const attendanceGridQuerySchema = paginationQuerySchema.extend({
    month,
    /** Name or Emp. Code (register only). */
    q: z.string().trim().max(100).optional(),
});
export const attendanceGridSchema = paginated(z.object({
    employee: memberSchema,
    /** One status per date of the month, in order; null when there is no record. */
    days: z.array(z.enum(DAY_STATUSES).nullable()),
    totals: attendanceTotalsSchema,
})).extend({ month, dates: z.array(isoDate) });
/** `GET /attendance/day?employeeId=&date=`: one person's day. */
export const attendanceDayQuerySchema = z.object({ employeeId: uuid, date: isoDate });
export const attendanceDaySchema = z.object({
    employee: memberSchema,
    record: dayRecordSchema,
    /** All punches of the day, voided ones too (oldest first). */
    punches: z.array(punchItemSchema.extend({
        note: z.string().nullable(),
        voided: z.boolean(),
        voidReason: z.string().nullable(),
        /** Location only for the employee and attendance.read.all (AC-9); otherwise null. */
        latitude: z.number().nullable(),
        longitude: z.number().nullable(),
        accuracyM: z.number().nullable(),
        ip: z.string().nullable(),
    })),
    /** The caller may see the location fields (self or attendance.read.all). */
    locationVisible: z.boolean(),
});
/** `GET /exports/attendance-summary.xlsx?month=` (attendance.read.all). */
export const attendanceSummaryExportQuerySchema = z.object({ month });
/**
 * `GET /attendance/summary/today` (attendance.read.all): how many people are in each state today
 * across the company, and — with attendance.manage — what waits for HR (T-07E dashboard).
 */
export const attendanceTodaySummarySchema = z.object({
    date: isoDate,
    /** People on the roster today. */
    total: z.int(),
    counts: z.record(z.enum(TODAY_STATES), z.int()),
    /** Flagged punches waiting for review; null without attendance.manage. */
    flagged: z.int().nullable(),
    /** Device users with unmapped punches; null without attendance.manage. */
    unmapped: z.int().nullable(),
});
//# sourceMappingURL=attendance.js.map